import { MoreHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ReachViewer } from '@/types';

/**
 * Horizontal "reach chain" from wireframe 4: the author ("Moi") linked by a
 * connector line to the people who discovered the song. When there are many
 * discoverers the avatars shrink and a clickable "…" opens the full list.
 */

function Avatar({
  url,
  name,
  size,
  ring,
}: {
  url?: string | null;
  name?: string | null;
  size: number;
  ring?: boolean;
}) {
  const initials = (name ?? '?').slice(0, 2).toUpperCase();
  return url ? (
    <img
      src={url}
      alt=""
      style={{ width: size, height: size }}
      className={cn(
        'tw:shrink-0 tw:rounded-full tw:object-cover',
        ring && 'tw:ring-2 tw:ring-dark-1',
      )}
    />
  ) : (
    <span
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.32) }}
      className={cn(
        'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:font-semibold tw:text-white',
        ring && 'tw:ring-2 tw:ring-dark-1',
      )}
    >
      {initials}
    </span>
  );
}

const Connector = () => (
  <span aria-hidden="true" className="tw:h-px tw:min-w-3 tw:flex-1 tw:bg-grey-4/50" />
);

export default function AvatarChain({
  me,
  viewers,
  reachCount,
  meLabel,
  overflowLabel,
  onMore,
  linkedViewers,
  linkedCount = 0,
  onLinkedMore,
}: {
  me: { avatar_url?: string | null; username?: string | null };
  viewers: ReachViewer[];
  reachCount: number;
  meLabel: string;
  /**
   * my-reco-rebound-clarity — renders the overflow ("… & 6 autres"). Passed in
   * so the component stays free of i18n, exactly like `meLabel`.
   */
  overflowLabel: (n: number) => string;
  /** Opens the full discoverers list. */
  onMore?: () => void;
  /** B5 — second batch linked by "…": people who discovered via a REBOUND. */
  linkedViewers?: ReachViewer[];
  linkedCount?: number;
  onLinkedMore?: () => void;
}) {
  // Shrink avatars as the crowd grows so a long chain still fits one row.
  const size = reachCount > 8 ? 26 : reachCount > 4 ? 32 : 40;
  const maxShown = reachCount > 8 ? 5 : 3;
  const shown = viewers.slice(0, maxShown);
  const hasMore = reachCount > shown.length;

  return (
    <div className="tw:flex tw:items-start tw:gap-2">
      {/* Me — the origin */}
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-1">
        <Avatar url={me.avatar_url} name={me.username} size={size} />
        <span className="tw:text-[11px] tw:text-grey-3">{meLabel}</span>
      </div>

      {reachCount > 0 && (
        <>
          <div className="tw:flex tw:flex-1 tw:items-center" style={{ height: size }}>
            <Connector />
          </div>

          {/* First node — the people who discovered the song straight from this
              reco. An explicit button (not just the card's wrapper onClick) so
              it is keyboard-reachable and the tap target is unambiguous. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMore?.();
            }}
            aria-label="Discovered from this recommendation"
            data-testid="avatar-chain-direct-batch"
            className="tw:flex tw:items-center tw:-space-x-1.5 tw:transition-opacity tw:active:opacity-70"
          >
            {shown.map((v, i) => (
              <span key={v.id} className="tw:flex tw:flex-col tw:items-center tw:gap-1">
                <Avatar url={v.avatar_url} name={v.username} size={size} ring />
                {i === 0 && size >= 32 && (
                  <span className="tw:max-w-16 tw:truncate tw:text-[11px] tw:text-grey-3">
                    {v.username}
                  </span>
                )}
              </span>
            ))}
            {/* my-reco-rebound-clarity — overflow is PRESENTATION: it is text
                inside this group, never a round token. A round token in this
                chain means one thing only: a rebound. */}
            {hasMore && (
              <span
                data-testid="avatar-chain-overflow"
                className="tw:ml-2 tw:shrink-0 tw:text-[11px] tw:font-semibold tw:text-grey-3"
              >
                {overflowLabel(reachCount - shown.length)}
              </span>
            )}
          </button>

          {/* B5 — the "…" link to the second batch: discovered through rebounds. */}
          {linkedCount > 0 && (
            <>
              <div className="tw:flex tw:flex-1 tw:items-center" style={{ height: size }}>
                <Connector />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLinkedMore?.();
                }}
                aria-label="Discovered through rebounds"
                data-testid="avatar-chain-rebound-batch"
                className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:transition-opacity tw:active:opacity-70"
              >
                <span
                  style={{ width: Math.round(size * 0.7), height: Math.round(size * 0.7) }}
                  className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-primary tw:text-grey-3 tw:ring-2 tw:ring-dark-1"
                >
                  <MoreHorizontal size={Math.round(size * 0.4)} />
                </span>
                <span className="tw:flex tw:items-center tw:-space-x-1.5">
                  {(linkedViewers ?? []).slice(0, 3).map((v) => (
                    <Avatar key={v.id} url={v.avatar_url} name={v.username} size={size} ring />
                  ))}
                </span>
                {linkedCount > 3 && (
                  <span className="tw:text-[11px] tw:font-semibold tw:text-grey-3">
                    +{linkedCount - 3}
                  </span>
                )}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
